import { HttpContext } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter, firstValueFrom } from 'rxjs';

import { MENU_MANAGEMENT } from '@core/constant/api.constants';
import { BaseService } from '@core/services/base.service';
import { SKIP_ERROR_TOAST } from '@core/services/auth.interceptor';
import { WebsocketService } from '@core/services/websocket.service';

/** Badges show at most this, then "99+". */
export const PENDING_COUNT_CAP = 99;

export type PendingCounts = Readonly<Record<string, number>>;

/** One push on `admin.pending-counts.updates`; a later `ts` is a newer count. */
export interface PendingCountChangedEvent {
  event: 'pending-count.changed';
  key: string;
  count: number;
  ts: string;
}

/** `{ key: n }`, also when wrapped in the standard `{ data }` envelope; non-numbers dropped. */
export function readPendingCounts(body: unknown): PendingCounts {
  const raw =
    body !== null && typeof body === 'object' && 'data' in body
      ? (body as { data: unknown }).data
      : body;
  if (raw === null || typeof raw !== 'object') return {};
  const counts: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'number' && Number.isFinite(value)) counts[key] = value;
  }
  return counts;
}

/** A well-formed pending-count push, or null for any other socket payload. */
export function readPendingCountEvent(payload: unknown): PendingCountChangedEvent | null {
  if (payload === null || typeof payload !== 'object') return null;
  const { event, key, count, ts } = payload as Record<string, unknown>;
  if (event !== 'pending-count.changed' || typeof key !== 'string' || typeof ts !== 'string') {
    return null;
  }
  if (typeof count !== 'number' || !Number.isFinite(count)) return null;
  return { event, key, count, ts };
}

/** Badge text for a count: nothing at 0, the number up to 99, then "99+". */
export function pendingBadgeText(count: number | undefined): string | null {
  if (count === undefined || count <= 0) return null;
  return count > PENDING_COUNT_CAP ? `${PENDING_COUNT_CAP}+` : String(count);
}

/**
 * Sidebar pending-count badges (A-D9). `GET /menu-management/pending-counts` runs once on start
 * (retried on navigation until it succeeds) and again after a socket reconnect; in between, the
 * counts follow the `admin.pending-counts.updates` pushes relayed by pulse-broker. No polling.
 *
 * The broadcast reaches every admin, so a push is applied only to a key the permission-filtered
 * `GET` returned — no badge ever appears for a screen the admin cannot open. Exposed as a signal
 * so the sidebar repaints only the badge; re-pushing the menu would rebuild the MetisMenu
 * accordion. A failed load keeps the last counts and is not toasted.
 */
@Injectable({ providedIn: 'root' })
export class PendingCountsService {
  private readonly base = inject(BaseService);
  private readonly router = inject(Router);
  private readonly websocket = inject(WebsocketService);

  readonly counts = signal<PendingCounts>({});

  private subscriptions: Subscription | null = null;
  private loaded = false;
  private inFlight = false;
  private readonly lastPushTs = new Map<string, string>();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  /** Loads the counts and starts following pushes; safe to call more than once. */
  start(): void {
    if (this.subscriptions !== null) return;
    const subscriptions = new Subscription();
    subscriptions.add(this.websocket.messages$.subscribe((payload) => this.applyPush(payload)));
    subscriptions.add(this.websocket.reconnected$.subscribe(() => void this.refresh()));
    subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd && !this.loaded))
        .subscribe(() => void this.refresh()),
    );
    this.subscriptions = subscriptions;
    this.websocket.subscribeToPendingCounts();
    void this.refresh();
  }

  stop(): void {
    if (this.subscriptions === null) return;
    this.subscriptions.unsubscribe();
    this.subscriptions = null;
    this.websocket.unsubscribeFromPendingCounts();
  }

  badgeFor(key: string | undefined): string | null {
    return key ? pendingBadgeText(this.counts()[key]) : null;
  }

  async refresh(): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    try {
      const body = await firstValueFrom(
        this.base.http.get(MENU_MANAGEMENT.PENDING_COUNTS, {
          context: new HttpContext().set(SKIP_ERROR_TOAST, true),
        }),
        { defaultValue: null },
      );
      if (body !== null) {
        this.counts.set(readPendingCounts(body));
        this.loaded = true;
      }
    } catch {
      // Keep the last counts; the next navigation (before the first load) or reconnect retries.
    } finally {
      this.inFlight = false;
    }
  }

  private applyPush(payload: unknown): void {
    const push = readPendingCountEvent(payload);
    if (!push) return;
    const current = this.counts();
    if (!(push.key in current)) return;
    // Publishers race (admin, exhibitor and external-api all push); keep the newest count.
    const last = this.lastPushTs.get(push.key);
    if (last !== undefined && last >= push.ts) return;
    this.lastPushTs.set(push.key, push.ts);
    if (current[push.key] !== push.count) this.counts.set({ ...current, [push.key]: push.count });
  }
}
